import { useCallback, useEffect, useMemo, useState } from 'react';
import { sortData } from '../lib/sortData';

const getSortedData = (
  initialData,
  sortedInfo,
  sortableColumnKeys,
  sortMode
) => {
  const { columnKey, order } = sortedInfo;

  const config = sortableColumnKeys[columnKey];

  let sorterKey;

  if (Array.isArray(config)) {
    sorterKey = config;
  } else {
    const modeKey =
      sortMode?.columnKey === columnKey
        ? sortMode?.mode
        : Object.keys(config)[0];

    sorterKey = config?.[modeKey];
  }

  return initialData.slice().sort((a, b) => sortData(order, sorterKey, a, b));
};

const useTableChange = ({ initialData, sortableColumnKeys, depsToReset }) => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [sortMode, setSortMode] = useState({
    columnKey: null,
    mode: null,
  });

  const handleTableChange = useCallback((_pagination, filters, sorter) => {
    const { columnKey } = sorter;

    setSortedInfo(sorter);

    setSortMode((prev) => {
      if (prev?.columnKey !== columnKey) {
        return { columnKey: null, mode: null };
      }
      return prev;
    });
  }, []);

  const handleSortMode = ({ columnKey, mode }) => {
    setSortMode({ columnKey, mode });

    if (!columnKey && !mode) {
      setSortedInfo(null);
    }
  };

  const data = useMemo(() => {
    const isSorted = Boolean(
      sortedInfo?.columnKey && sortedInfo?.order && sortableColumnKeys
    );

    return isSorted
      ? getSortedData(initialData, sortedInfo, sortableColumnKeys, sortMode)
      : initialData;
  }, [initialData, sortableColumnKeys, sortedInfo, sortMode]);

  useEffect(() => {
    setSortedInfo(null);
  }, [...depsToReset]);

  return { data, sortedInfo, handleTableChange, handleSortMode, sortMode };
};

export default useTableChange;
